import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatchScoreRingComponent } from '../../../shared/match-score-ring/match-score-ring.component';

export type LeadStatus = 'new' | 'contacted' | 'opened' | 'replied';
export type MatchTier = 'Basic' | 'Good' | 'Excellent';

export interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  company: string;
  location: string;
  avatarColor: string;
  status: LeadStatus;
  matchPercentage: number;
  matchTier: MatchTier;
  isBookmarked: boolean;
  feedback: 'positive' | 'negative' | null;
  createdAt?: string;
  sentAt?: string;
  openedCount?: number;
  whyThisLead?: string;
  linkedinUrl?: string | null;
  reportId?: string | null;
  photoUrl?: string | null;
  departments?: string[];
  companyLogoUrl?: string | null;
}

@Component({
  selector: 'app-lead-card-legacy',
  standalone: true,
  imports: [CommonModule, MatchScoreRingComponent],
  templateUrl: './lead-card.component.html',
  styleUrl: './lead-card.component.scss'
})
export class LeadCardLegacyComponent {
  @Input() lead: Lead;
  @Input() isGenerating = false;
  @Input() hasReport = false;
  @Output() bookmarkToggle = new EventEmitter<Lead>();
  @Output() feedbackGiven = new EventEmitter<{ lead: Lead; type: 'positive' | 'negative' }>();
  @Output() viewProfile = new EventEmitter<Lead>();
  @Output() sendEmail = new EventEmitter<Lead>();
  @Output() viewDetails = new EventEmitter<Lead>();

  showWhyThisLead = false;

  get initials(): string {
    return (this.lead.firstName[0] + this.lead.lastName[0]).toUpperCase();
  }

  get fullName(): string {
    return `${this.lead.firstName} ${this.lead.lastName}`;
  }

  get isNew(): boolean {
    if (!this.lead.createdAt) return false;
    const created = new Date(this.lead.createdAt);
    const today = new Date();
    return created.getFullYear() === today.getFullYear()
      && created.getMonth() === today.getMonth()
      && created.getDate() === today.getDate();
  }

  get statusLabel(): string {
    switch (this.lead.status) {
      case 'contacted': return 'Sent';
      case 'opened': return `Opened${this.lead.openedCount ? ' ' + this.lead.openedCount + 'x' : ''}`;
      case 'replied': return 'Replied';
      default: return '';
    }
  }

  get matchBarColor(): string {
    if (this.lead.matchPercentage >= 80) return 'bg-green-500';
    if (this.lead.matchPercentage >= 60) return 'bg-orange-400';
    return 'bg-red-400';
  }

  toggleBookmark(): void {
    this.lead.isBookmarked = !this.lead.isBookmarked;
    this.bookmarkToggle.emit(this.lead);
  }

  giveFeedback(type: 'positive' | 'negative'): void {
    this.lead.feedback = this.lead.feedback === type ? null : type;
    this.feedbackGiven.emit({ lead: this.lead, type });
  }

  onViewProfile(): void {
    this.viewProfile.emit(this.lead);
  }

  onSendEmail(): void {
    this.sendEmail.emit(this.lead);
  }

  onViewDetails(): void {
    this.viewDetails.emit(this.lead);
  }

  toggleWhyThisLead(): void {
    this.showWhyThisLead = !this.showWhyThisLead;
  }
}
